import { useState, useRef } from "react";
import { UploadSimple } from "@phosphor-icons/react";
import { ToolShell } from "@/components/ToolShell";
import { useTrackOnce } from "@/hooks/useTrackOnce";
import { useSeo } from "@/lib/seo";

const Thumb = ({ src, duration, className = "", rounded = "rounded-xl" }) => (
  <div className={`relative overflow-hidden ${rounded} ${className}`}>
    <img src={src} alt="thumbnail preview" className="w-full h-full object-cover aspect-video" />
    <span className="absolute bottom-1 right-1 bg-black/85 text-white text-[10px] font-semibold px-1 py-0.5 rounded">{duration}</span>
  </div>
);

const Meta = ({ title, small }) => (
  <div className={small ? "mt-0" : "mt-2.5"}>
    <p className={`font-semibold leading-snug ${small ? "text-xs" : "text-sm"} line-clamp-2`}>{title}</p>
    <p className={`text-[var(--muted)] ${small ? "text-[10px]" : "text-xs"} mt-1`}>Your Channel · 12K views · 3 hours ago</p>
  </div>
);

export default function ThumbnailPreview() {
  useSeo("Thumbnail UI Previewer", "Upload your thumbnail and see exactly how it looks on YouTube's homepage, mobile feed and sidebar — with the duration badge overlaid.");
  const [img, setImg] = useState(null);
  const [title, setTitle] = useState("My Amazing Video Title Goes Right Here");
  const [duration, setDuration] = useState("12:34");
  const inputRef = useRef(null);
  const track = useTrackOnce("thumbnail_preview");

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setImg(reader.result); track(); };
    reader.readAsDataURL(f);
  };

  return (
    <ToolShell slug="thumbnail-preview" title="Thumbnail UI Previewer" tagline="See your thumbnail exactly as viewers will — desktop homepage, mobile feed and sidebar — with the duration badge in place so nothing important gets covered." hasResults={!!img}>
      <div className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <input ref={inputRef} type="file" accept="image/*" onChange={onPick} className="hidden" data-testid="preview-file-input" />
        <button
          data-testid="preview-upload-btn"
          onClick={() => inputRef.current?.click()}
          className="w-full border-2 border-dashed border-[var(--border-c)] hover:border-[var(--glow)] rounded-[14px] py-8 flex flex-col items-center gap-2 transition-colors"
        >
          <UploadSimple size={24} color="var(--glow)" weight="duotone" />
          <span className="font-semibold text-sm">{img ? "Swap thumbnail" : "Upload your thumbnail"}</span>
          <span className="text-xs text-[var(--muted)]">1280×720 recommended — never leaves your browser</span>
        </button>
        <div className="grid sm:grid-cols-[1fr_120px] gap-3 mt-4">
          <input data-testid="preview-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Video title" className="bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-2.5 text-sm outline-none focus:border-[var(--glow)]" />
          <input data-testid="preview-duration-input" value={duration} onChange={(e) => setDuration(e.target.value)} placeholder="12:34" className="bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-2.5 text-sm outline-none focus:border-[var(--glow)]" />
        </div>
      </div>

      {img && (
        <div className="mt-8 space-y-6 fade-up" data-testid="preview-surfaces">
          <div className="surface rounded-[20px] p-6">
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-semibold mb-4">Desktop homepage</p>
            <div className="max-w-[340px]">
              <Thumb src={img} duration={duration} />
              <Meta title={title} />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="surface rounded-[20px] p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-semibold mb-4">Sidebar / suggested</p>
              <div className="flex gap-2.5 max-w-[360px]">
                <Thumb src={img} duration={duration} className="w-40 shrink-0" rounded="rounded-lg" />
                <Meta title={title} small />
              </div>
            </div>
            <div className="surface rounded-[20px] p-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[var(--muted)] font-semibold mb-4">Mobile feed</p>
              <div className="max-w-[300px] mx-auto border border-[var(--border-c)] rounded-[18px] p-2 bg-[var(--bg)]">
                <Thumb src={img} duration={duration} rounded="rounded-lg" />
                <div className="px-1 pb-1"><Meta title={title} /></div>
              </div>
            </div>
          </div>
          <p className="text-xs text-[var(--muted)]">Tip: keep text out of the bottom-right corner — the duration badge always covers it.</p>
        </div>
      )}
    </ToolShell>
  );
}
